import React from "react"
import styled from "styled-components"
import { Link } from "gatsby"

// Styles
import colors from "../../../styles/colors"
import { fonts } from "../../../styles/fonts"
import { fontWeights } from "../../../styles/textStyles"
import mq from "../../../styles/breakpoints"
import { fastTransition } from "../../../styles/animations"

// Components
import ArrowRight from "../../icons/ArrowRight"

// Hooks
import { useLocale } from "../../../hooks/useLocale"

export interface ICaseStudy {
  slug: string
  title: string
  companyName: string
  companySector: string
  imageUrl: string
}

interface IProps {
  caseStudy: ICaseStudy
}

const CaseStudyCard: React.FC<IProps> = ({ caseStudy }) => {
  const { getLocalizedLink } = useLocale()

  return (
    <CardLink
      to={getLocalizedLink(`/cases/${caseStudy.slug}`)}
      imageUrl={caseStudy.imageUrl}
      aria-label={caseStudy.title}
    >
      <CardContent>
        <Badge>Case study</Badge>
        <BottomContent>
          <Title>{caseStudy.title}</Title>
          <Tags>
            <Tag>{caseStudy.companyName}</Tag>
            <Tag>{caseStudy.companySector}</Tag>
          </Tags>
          <ReadMore>
            <ReadMoreText>Read more</ReadMoreText>
            <ArrowRight width={24} height={24} color={colors.white} />
          </ReadMore>
        </BottomContent>
      </CardContent>
    </CardLink>
  )
}

export default CaseStudyCard

const CardLink = styled(Link)<{ imageUrl: string }>`
  display: block;
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid ${colors.main["500"]};
  background: linear-gradient(
      180deg,
      rgba(29, 22, 54, 0) 0%,
      rgba(29, 22, 54, 0.65) 100%
    ),
    ${({ imageUrl }) => `url(${imageUrl})`} lightgray 50% / cover no-repeat;
  box-shadow: 0px 8px 24px 4px rgba(0, 0, 0, 0.08),
    0px 8px 12px 0px rgba(54, 42, 102, 0.1);
  text-decoration: none;
  height: 345px;
  transition: border-color ${fastTransition};

  &:hover {
    border-color: ${colors.purpleLight};
    background: linear-gradient(
        180deg,
        rgba(29, 22, 54, 0) 0%,
        rgba(29, 22, 54, 1) 100%
      ),
      ${({ imageUrl }) => `url(${imageUrl})`} lightgray 50% / cover no-repeat;
  }

  ${mq.from.M`
    height: 411px;
  `}
`

const CardContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 24px;

  ${mq.from.M`
    padding: 40px;
  `}
`

const Badge = styled.h4`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 8px 12px;
  background-color: ${colors.purpleLight};
  border-radius: 8px;
  font-family: ${fonts.primary};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 16px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${colors.white};

  ${mq.from.M`
    font-size: 16px;
  `}
`

const BottomContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${mq.from.M`
    gap: 24px;
  `}
`

const Title = styled.h3`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 24px;
  font-weight: ${fontWeights.bold};
  line-height: 34px;
  letter-spacing: -1px;
  color: ${colors.white};

  ${mq.from.M`
    font-size: 32px;
    line-height: 40px;
  `}
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid ${colors.white};
  border-radius: 50px;
  font-family: ${fonts.primary};
  font-size: 12px;
  font-weight: ${fontWeights.regular};
  line-height: 16px;
  letter-spacing: -0.12px;
  color: ${colors.white};
  white-space: nowrap;

  ${mq.from.M`
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.14px;
  `}
`

const ReadMore = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  transition: gap ${fastTransition};

  ${CardLink}:hover & {
    gap: 24px;
  }
`

const ReadMoreText = styled.span`
  font-family: ${fonts.primary};
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 18px;
  color: ${colors.white};

  ${mq.from.M`
    font-size: 18px;
    line-height: 20px;
  `}
`
